import React, { ReactElement, FC, useState } from 'react'
import { Card, Typography } from 'antd'
import { Attachments } from './Attachments'
import { s } from './style'
import { getAddress } from '../../../../Graphql-Utilities'
import {
  formatCents,
  createCompressedImgxImageUrl,
} from '../../../../Utilities'
import { useDateTimeDuration } from '../../../../Hooks'
import { RedClockIcon } from './RedClockIcon'
import { ProposalModal } from '../../ProposalModal'
import {
  ConfirmedBookingEventFragment,
  ConfirmedBookingProposalFragment,
} from './ConfirmedBooking.generated-types'
import { Suppliers } from '../../../../Components/Suppliers'
import { SustainableEventBannerWrapper } from '../../../../Components/SustainableEventBannerWrapper'
import { GOOGLE_MAPS, ROUTES } from '../../../../Constants'
import { Link } from 'react-router-dom'

const { Text, Title } = Typography

const MAIN_SPACE_PHOTO_HEIGHT: number = 334
const SECONDARY_SPACE_PHOTOS_HEIGHT: number = 167

interface ConfirmedBookingProps {
  event: ConfirmedBookingEventFragment
  proposal: ConfirmedBookingProposalFragment
}

export const ConfirmedBooking: FC<ConfirmedBookingProps> = ({
  event,
  event: { name, currency, supplierDeals },
  proposal,
  proposal: {
    baseCostTotal,
    space: { spacePhotos, spaceName, venue },
  },
}): ReactElement => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const { durationFullDate, durationRange } = useDateTimeDuration(event)

  const address = getAddress(proposal)

  const calculateCostTotalWithSupplierDeals = (): number => {
    return supplierDeals.length
      ? supplierDeals.map((deal) => deal.cost).reduce((a, b) => a + b) +
          baseCostTotal
      : baseCostTotal
  }

  const handleCloseModal = (): void => setIsModalVisible(false)

  return (
    <>
      <div style={s.root}>
        <div>
          <div style={s.firstRow}>
            <Title level={3} style={{ margin: 0 }}>
              {name}
            </Title>
            <Text style={{ fontSize: '24px' }}>{`Total spend (${
              currency === 'AUD' ? 'inc GST' : 'ex VAT'
            })`}</Text>
          </div>
          <div style={s.secondRow}>
            <Text style={{ color: '#595959' }}>{`${durationFullDate}${
              durationRange ? ', ' + durationRange : ''
            }`}</Text>
            <Text strong>
              {formatCents(currency, calculateCostTotalWithSupplierDeals())}
            </Text>
          </div>
        </div>
        <Card
          style={{
            marginTop: '24px',
          }}
          bodyStyle={s.cardBody}
        >
          {spacePhotos && spacePhotos.length > 2 ? (
            <div style={s.images.root}>
              <img
                style={s.images.primary}
                src={createCompressedImgxImageUrl({ originalImgxImageUrl: spacePhotos[0].url, height: MAIN_SPACE_PHOTO_HEIGHT })}
                alt="Main space photo"
                height={MAIN_SPACE_PHOTO_HEIGHT}
              />
              <div style={s.images.side}>
                <img
                  style={s.images.sideImages}
                  src={createCompressedImgxImageUrl({ originalImgxImageUrl: spacePhotos[1].url, height: SECONDARY_SPACE_PHOTOS_HEIGHT })}
                  alt="Second space photo"
                  height={SECONDARY_SPACE_PHOTOS_HEIGHT}
                />
                <img
                  style={s.images.sideImages}
                  src={createCompressedImgxImageUrl({ originalImgxImageUrl: spacePhotos[2].url, height: SECONDARY_SPACE_PHOTOS_HEIGHT })}
                  alt="Third space photo"
                  height={SECONDARY_SPACE_PHOTOS_HEIGHT}
                />
              </div>
            </div>
          ) : spacePhotos && spacePhotos.length > 0 ? (
            <img
              style={s.image}
              src={createCompressedImgxImageUrl({ originalImgxImageUrl: spacePhotos[0].url, height: MAIN_SPACE_PHOTO_HEIGHT })}

              alt="Main space photo"
              height={MAIN_SPACE_PHOTO_HEIGHT}
            />
          ) : undefined}
          <div
            style={{
              margin: '30px',
              display: 'grid',
              gridTemplateColumns: '60px 1fr 140px',
              gridTemplateRows: 'auto auto auto',
              gridTemplateAreas:
                '"tick details details" "tick name blank" "tick address cost"',
              rowGap: 8,
              columnGap: 24,
              fontSize: 18,
              lineHeight: '24px',
            }}
          >
            <RedClockIcon style={s.tick} />
            <div
              style={{
                gridArea: 'details',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Text style={{ marginBottom: '8px' }}>
                We’re just finalising the details - we’ll be in touch soon to
                confirm your booking
              </Text>
            </div>
            <div
              style={{
                gridArea: 'name',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {
                // These shouldn't really be nullable but since they are, it is better to show nothing instead of crashing the whole application if they somehow are null
                venue?.name && spaceName ? (
                  <Text style={{ fontSize: '24px' }}>
                    {`${venue.name}, ${spaceName}`}{' '}
                    <span style={{ fontWeight: 600, marginLeft: 20 }}>
                      {formatCents(currency, baseCostTotal)}
                    </span>
                  </Text>
                ) : null
              }
            </div>
            <div
              style={{
                gridArea: 'cost',
                alignSelf: 'end',
                fontSize: 24,
                overflowWrap: 'unset',
              }}
            >
              {
                // These shouldn't really be nullable but since they are, it is better to show nothing instead of crashing the whole application if they somehow are null
                venue?.name && spaceName ? (
                  <div>
                    <Link
                      to={`${ROUTES.PROPOSAL}/${proposal.UUID}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      style={{
                        gridArea: 'details',
                        fontSize: 18,
                        fontWeight: 600,
                        color: '#1270B8',
                        paddingLeft: '12px',
                        paddingRight: '12px',
                      }}
                    >
                      View details
                    </Link>
                  </div>
                ) : null
              }
            </div>
            {address && (
              <div
                style={{
                  gridArea: 'address',
                  display: 'flex',
                }}
              >
                <Text>{address}</Text>
                <a
                  href={`${GOOGLE_MAPS}${address}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  style={{ color: '#1270B8', paddingLeft: '12px' }}
                >
                  Show on a map
                </a>
              </div>
            )}
          </div>
          <Attachments proposal={proposal} strong style={{ margin: 32 }} />
        </Card>
        <SustainableEventBannerWrapper
          eventUUID={event.UUID}
          marginTop={8}
          marginBottom={8}
        />
        <div style={{ marginTop: 8 }}>
          <Suppliers supplierDeals={supplierDeals} />
        </div>
      </div>
      <ProposalModal
        modalVisible={isModalVisible}
        handleCloseModal={handleCloseModal}
        response={proposal}
        canRequestToBook={false}
        event={event}
      />
    </>
  )
}
